import { useMemo } from 'react';
import { DoubleSide } from 'three';

interface Pixel {
  r: number;
  g: number;
  b: number;
}

interface ExtrudedSurfaceProps {
  pixels: Pixel[];
  imageWidth: number;
  imageHeight: number;
  showLabels: boolean;
}

function calculateDistanceToGrayscaleDiagonal(r: number, g: number, b: number): number {
  const px = r, py = g, pz = b;
  const vx = 255, vy = 255, vz = 255;
  const dot = px * vx + py * vy + pz * vz;
  const vLen2 = vx * vx + vy * vy + vz * vz;
  const t = Math.max(0, Math.min(1, dot / vLen2));
  const cx = t * vx, cy = t * vy, cz = t * vz;
  const dx = px - cx, dy = py - cy, dz = pz - cz;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

export default function ExtrudedSurface({ pixels, imageWidth, imageHeight }: ExtrudedSurfaceProps) {
  const geometryData = useMemo(() => {
    if (!pixels || pixels.length === 0 || imageWidth <= 0 || imageHeight <= 0) {
      return null;
    }

    const MAX_SIDE = 64;
    const ar = imageWidth / imageHeight;
    const cols = ar >= 1 ? MAX_SIDE : Math.max(2, Math.round(MAX_SIDE * ar));
    const rows = ar >= 1 ? Math.max(2, Math.round(MAX_SIDE / ar)) : MAX_SIDE;

    const vertices: number[] = [];
    const indices: number[] = [];

    const xHalf = 0.5;
    const yHalf = ar >= 1 ? 0.5 / ar : 0.5 * ar;

    const rowStep = imageHeight / (rows - 1);
    const colStep = imageWidth / (cols - 1);

    // Increased by 5x
    const DEFAULT_EXTRUSION = 1.0;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const pxIdxY = Math.min(imageHeight - 1, Math.max(0, Math.floor(r * rowStep)));
        const pxIdxX = Math.min(imageWidth - 1, Math.max(0, Math.floor(c * colStep)));
        const idx = pxIdxY * imageWidth + pxIdxX;

        let rr = 0, gg = 0, bb = 0;
        if (idx >= 0 && idx < pixels.length) {
          const p = pixels[idx];
          rr = p.r; gg = p.g; bb = p.b;
        }

        const dist = calculateDistanceToGrayscaleDiagonal(rr, gg, bb);
        const normalized = Math.min(1, dist / 255);
        // Invert Z so relief appears "up" toward the camera in top-down view
        const z = -normalized * DEFAULT_EXTRUSION;

        const x = -xHalf + (c / (cols - 1)) * (xHalf * 2);
        // Flip Y so image row 0 is at the top of the plane
        const y = yHalf - (r / (rows - 1)) * (yHalf * 2);

        vertices.push(x, y, z);
      }
    }

    for (let r = 0; r < rows - 1; r++) {
      for (let c = 0; c < cols - 1; c++) {
        const i0 = r * cols + c;
        const i1 = (r + 1) * cols + c;
        const i2 = r * cols + (c + 1);
        const i3 = (r + 1) * cols + (c + 1);
        indices.push(i0, i1, i2);
        indices.push(i1, i3, i2);
      }
    }

    return { vertices, indices };
  }, [pixels, imageWidth, imageHeight]);

  const ar = imageWidth > 0 && imageHeight > 0 ? imageWidth / imageHeight : 1;
  const planeWidth = ar >= 1 ? 1 : ar;
  const planeHeight = ar >= 1 ? 1 / ar : 1;

  return (
    <group>
      {geometryData ? (
        <mesh>
          <bufferGeometry onUpdate={(g) => g.computeVertexNormals()}>
            <bufferAttribute
              attach="attributes-position"
              count={geometryData.vertices.length / 3}
              array={new Float32Array(geometryData.vertices)}
              itemSize={3}
            />
            <bufferAttribute
              attach="index"
              count={geometryData.indices.length}
              array={new Uint16Array(geometryData.indices)}
              itemSize={1}
            />
          </bufferGeometry>
          <meshStandardMaterial color="#4a90e2" metalness={0.1} roughness={0.8} side={DoubleSide} />
        </mesh>
      ) : (
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[planeWidth, planeHeight, 2, 2]} />
          <meshStandardMaterial color="#4a90e2" metalness={0.1} roughness={0.8} side={DoubleSide} />
        </mesh>
      )}
    </group>
  );
}
